import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { fmtDate, fmtNum, LOCATION_LABELS, TXN_TYPE_LABELS } from "../lib/format";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function ItemHistoryModal({ item, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!item) return;
    setLoading(true);
    setError(false);
    api.get(`/reports/item-history/${item.item_id || item.id}`)
      .then(({ data }) => setRows(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [item]);

  // Compute running balance from oldest -> newest, excluding SOURCE_EXTERNAL contra legs
  // (each opening/purchase writes a mirrored OUT row at SOURCE_EXTERNAL for double-entry).
  const withBalance = useMemo(() => {
    const real = rows.filter(r => r.location !== "SOURCE_EXTERNAL");
    const sorted = [...real].sort((a, b) => (a.server_timestamp || "").localeCompare(b.server_timestamp || ""));
    let bal = 0;
    const withBal = sorted.map(r => {
      bal += (r.direction || 0) * (r.qty || 0);
      return { ...r, running: bal };
    });
    return withBal.reverse();
  }, [rows]);

  // Daily running-balance series (oldest -> newest) for the movement chart
  const chartSeries = useMemo(() => {
    const byDay = new Map();
    [...withBalance].reverse().forEach(r => {
      const day = (r.operational_date || r.server_timestamp || "").slice(0, 10);
      if (day) byDay.set(day, r.running); // last movement of the day wins
    });
    return Array.from(byDay.entries()).map(([day, balance]) => ({ day, balance }));
  }, [withBalance]);

  return (
    <Dialog open={!!item} onOpenChange={() => onClose && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="item-history-modal">
        <DialogHeader>
          <DialogTitle>
            <span className="font-serif text-xl">{item?.name}</span>
            {item?.size && <span className="ml-2 text-sm text-slate-500">· {item.size}</span>}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Loading history…</div>
        ) : error ? (
          <div className="text-sm text-red-600 py-8 text-center" data-testid="item-history-error">Could not load history. Please try again.</div>
        ) : withBalance.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">No movements yet.</div>
        ) : (
          <>
            {chartSeries.length > 1 && (
              <div className="mb-4" data-testid="item-history-chart">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Balance over time</div>
                <div style={{ width: "100%", height: 180 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartSeries} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", fontSize: 12 }} />
                      <Line type="stepAfter" dataKey="balance" stroke="#0f172a" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
            <table className="vr-table" data-testid="item-history-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Direction</th>
                  <th>Location</th>
                  <th className="num">Qty</th>
                  <th className="num">Running</th>
                  <th>By</th>
                  <th>Reason / Remarks</th>
                </tr>
              </thead>
              <tbody>
                {withBalance.map((r, i) => {
                  const variance = r.txn_type?.startsWith("correction") || r.txn_type?.startsWith("adjustment") || r.txn_type === "approved_loss" || r.txn_type === "disposal";
                  return (
                    <tr key={`${r.server_timestamp || ""}-${i}`} className={variance ? "bg-amber-50" : ""}>
                      <td className="whitespace-nowrap">{fmtDate(r.server_timestamp)}</td>
                      <td>
                        <Badge className={variance ? "bg-amber-100 border text-amber-800" : "bg-slate-100 border text-slate-700"}>
                          {TXN_TYPE_LABELS[r.txn_type] || r.txn_type}
                        </Badge>
                      </td>
                      <td className={r.direction === 1 ? "text-emerald-700" : "text-red-700"}>{r.direction === 1 ? "IN" : "OUT"}</td>
                      <td className="text-xs text-slate-600">{LOCATION_LABELS[r.location] || r.location}</td>
                      <td className="num font-semibold">{fmtNum(r.qty)}</td>
                      <td className="num text-slate-800 font-semibold">{fmtNum(r.running)}</td>
                      <td className="text-xs">{r.submitted_by_name || "—"}</td>
                      <td className="text-xs text-slate-600 max-w-xs truncate" title={`${r.reason || ""} ${r.remarks || ""}`}>{r.reason || r.remarks || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
